// line-charts.tsx (NutritionTrendsWeb: now includes MacroRingsWeb + charts layout)
//
// npm i recharts
//
import * as React from "react";
import { Box, Divider, Typography, useTheme, Grid } from "@mui/material";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import MacroRingsWeb from "../../../home/food-ring";
import { User, Meal } from "../../../../models/types";
import GlassContainer from "../../components/liquid-glass";

type FieldKey = "calories" | "protein" | "carbs" | "fats";

interface NutritionTrendsWebProps {
  user: User;
  /** Inclusive range */
  rangeStart: Date | string;
  rangeEnd: Date | string;
  chartHeight?: number;
  showGoals?: boolean;
}

/* ----------------------- Helpers ----------------------- */
const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
const ymd = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const dd = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
};
const parseDateLike = (v: Date | string): Date =>
  typeof v === "string" ? new Date(v) : new Date(v);

const enumerateDaysInclusive = (start: Date, end: Date): string[] => {
  const out: string[] = [];
  const s = startOfDay(start).getTime();
  const e = startOfDay(end).getTime();
  const MS = 86_400_000;
  const days = Math.max(1, Math.floor((e - s) / MS) + 1);
  for (let i = 0; i < days; i++) out.push(ymd(new Date(s + i * MS)));
  return out;
};

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

/** Robustly formats YYYY-MM-DD (or anything datey) to M/D, returns "" if unknown */
const niceDateTick = (val: unknown): string => {
  if (val === undefined || val === null) return "";
  const s = String(val);
  const parts = s.split("-");
  if (parts.length === 3) {
    const mm = Number(parts[1]);
    const dd = Number(parts[2]);
    if (!Number.isNaN(mm) && !Number.isNaN(dd)) return `${mm}/${dd}`;
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return `${d.getMonth() + 1}/${d.getDate()}`;
  return s;
};

const fieldMeta: Record<
  FieldKey,
  { label: string; unit: string; goalKey: keyof User["goals"] }
> = {
  calories: { label: "Calories", unit: "", goalKey: "caloriesGoal" },
  protein: { label: "Protein", unit: " g", goalKey: "proteinGoal" },
  carbs: { label: "Carbs", unit: " g", goalKey: "carbsGoal" },
  fats: { label: "Fats", unit: " g", goalKey: "fatsGoal" },
};

function buildDailySeries(
  meals: Meal[],
  start: Date,
  end: Date
): Array<{
  date: string;
  calories: number;
  protein: number;
  carbs: number;
  fats: number;
}> {
  const days = enumerateDaysInclusive(start, end);
  const map = new Map<
    string,
    { calories: number; protein: number; carbs: number; fats: number }
  >();
  for (const d of days)
    map.set(d, { calories: 0, protein: 0, carbs: 0, fats: 0 });

  for (const m of meals ?? []) {
    const d = ymd(new Date(m.dateTime));
    if (!map.has(d)) continue;
    const s =
      typeof m.servings === "number" && !isNaN(m.servings) ? m.servings : 1;
    const nz = (v?: number) => (typeof v === "number" && !isNaN(v) ? v : 0);
    const bucket = map.get(d)!;
    bucket.calories += Math.round(nz(m.calories) * s);
    bucket.protein += nz(m.protein) * s;
    bucket.carbs += nz(m.carbs) * s;
    bucket.fats += nz(m.fats) * s;
  }

  return days.map((d) => ({ date: d, ...(map.get(d) as any) }));
}

function averageOfField(data: any[], field: FieldKey): number {
  if (!data.length) return 0;
  return sum(data.map((row) => row[field] ?? 0)) / data.length;
}

const formatRangeTitle = (start: Date, end: Date) => {
  const sameDay = start.toDateString() === end.toDateString();
  if (sameDay) return "Today (Avg/Day)";
  return `${start.toLocaleDateString()} – ${end.toLocaleDateString()} (Avg/Day)`;
};

/* ----------------------- Tooltip ----------------------- */
const ChartTooltip = ({
  active,
  payload,
  label,
  field,
}: {
  active?: boolean;
  payload?: any[];
  label?: unknown;
  field: FieldKey;
}) => {
  if (!active || !payload || !payload.length) return null;
  const val = payload[0]?.value ?? 0;
  const display = niceDateTick(label);
  return (
    <Box
      sx={{
        bgcolor: "background.paper",
        border: (t) => `1px solid ${t.palette.divider}`,
        borderRadius: 1.5,
        p: 1,
      }}
    >
      <Typography variant="caption">{display}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {fieldMeta[field].label}: {Math.round(val)}
        {fieldMeta[field].unit}
      </Typography>
    </Box>
  );
};

/* ----------------------- Component ----------------------- */
export default function NutritionTrendsWeb({
  user,
  rangeStart,
  rangeEnd,
  chartHeight = 220,
  showGoals = true,
}: NutritionTrendsWebProps) {
  const theme = useTheme();

  const start = React.useMemo(
    () => startOfDay(parseDateLike(rangeStart)),
    [rangeStart]
  );
  const end = React.useMemo(
    () => endOfDay(parseDateLike(rangeEnd)),
    [rangeEnd]
  );

  const series = React.useMemo(
    () => buildDailySeries(user?.meals ?? [], start, end),
    [user?.meals, start, end]
  );

  // Averages for rings
  const avgCalories = Math.round(averageOfField(series, "calories"));
  const avgProtein = Math.round(averageOfField(series, "protein"));
  const avgCarbs = Math.round(averageOfField(series, "carbs"));
  const avgFats = Math.round(averageOfField(series, "fats"));

  const g = user?.goals ?? ({} as User["goals"]);

  const hasGoal = (k: keyof User["goals"]) =>
    showGoals && typeof (g as any)[k] === "number" && !isNaN((g as any)[k]);

  const glassBg = (t: typeof theme) =>
    t.palette.mode === "dark"
      ? "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.02) 100%)"
      : "linear-gradient(135deg, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.10) 100%)";

  const renderChart = (field: FieldKey) => {
    const goalValue = (g as any)[fieldMeta[field].goalKey] as
      | number
      | undefined;
    const showRef = hasGoal(fieldMeta[field].goalKey);

    return (
      <GlassContainer
        blur={20}
        radius={18}
        sx={{
          height: "100%",
          flexDirection: "column",
          p: 2.5,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            mb: 1,
            gap: 2,
            width: "100%",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {fieldMeta[field].label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ mr: 0.5 }}
            >
              Average:
            </Typography>
            <strong>
              {Math.round(averageOfField(series, field))}
              {fieldMeta[field].unit}
            </strong>
            {showRef && (
              <>
                <Typography component="span" variant="caption" sx={{ mx: 1 }}>
                  •
                </Typography>
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                >
                  Goal: {Math.round(goalValue as number)}
                  {fieldMeta[field].unit}/day
                </Typography>
              </>
            )}
          </Typography>
        </Box>

        <Divider sx={{ mb: 1.5, width: "100%" }} />

        {!series.length ? (
          <Typography variant="body2" color="text.secondary">
            No data in this range.
          </Typography>
        ) : (
          <Box sx={{ width: "100%", height: chartHeight }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={series}
                margin={{ top: 8, right: 16, bottom: 8, left: 8 }}
              >
                <CartesianGrid
                  stroke={theme.palette.mode === "light" ? "#eee" : "#333"}
                  strokeDasharray="3 3"
                />
                <XAxis
                  dataKey="date"
                  tickFormatter={niceDateTick}
                  interval="preserveStartEnd"
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  axisLine={{ stroke: theme.palette.divider }}
                  tickLine={{ stroke: theme.palette.divider }}
                  allowDuplicatedCategory={false}
                />
                <YAxis
                  width={48}
                  tick={{ fontSize: 12, fill: theme.palette.text.secondary }}
                  axisLine={{ stroke: theme.palette.divider }}
                  tickLine={{ stroke: theme.palette.divider }}
                  allowDecimals={false}
                />
                <RTooltip
                  content={({ active, payload, label }) => (
                    <ChartTooltip
                      active={active}
                      payload={payload}
                      label={label}
                      field={field}
                    />
                  )}
                />
                {showRef && (
                  <ReferenceLine
                    y={goalValue as number}
                    stroke={theme.palette.primary.main}
                    strokeDasharray="6 6"
                    ifOverflow="extendDomain"
                  />
                )}
                <Line
                  type="monotone"
                  dataKey={field}
                  stroke={theme.palette.primary.main}
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </GlassContainer>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {/* Row 1: Rings + Calories */}
        <Grid item xs={12} md={6}>
          <GlassContainer
            blur={22}
            radius={20}
            sx={{
              p: 2,
              height: "100%",
              alignItems: "stretch",
            }}
          >
            <Box
              sx={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: 2,
              }}
            >
              <Typography sx={{ mt: 2 }} fontSize={20} fontWeight={800}>
                Quick <br />
                Snapshot
              </Typography>
              <MacroRingsWeb
                isLightMode={theme.palette.mode === "light"}
                title={formatRangeTitle(start, end)}
                caloriesToday={avgCalories}
                caloriesGoal={Math.round((g as any).caloriesGoal ?? 0)}
                proteinToday={avgProtein}
                proteinGoal={Math.round((g as any).proteinGoal ?? 0)}
                carbsToday={avgCarbs}
                carbsGoal={Math.round((g as any).carbsGoal ?? 0)}
                fatToday={avgFats}
                fatGoal={Math.round((g as any).fatsGoal ?? 0)}
              />
            </Box>
          </GlassContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          {renderChart("calories")}
        </Grid>

        {/* Row 2: Protein, Carbs, Fats */}
        <Grid item xs={12} md={4}>
          {renderChart("protein")}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderChart("carbs")}
        </Grid>
        <Grid item xs={12} md={4}>
          {renderChart("fats")}
        </Grid>
      </Grid>
    </Box>
  );
}
